/**
 * Anteprima dei task scritti una riga per uno nella finestra Nuovo task
 * (7/10/2026). Il testo lo divide il divisore (packages/shared, dividiTesto):
 * qui si vede cosa si creerà, riga per riga, e si corregge prima del tasto
 * solo "Crea N task".
 *
 * Ogni riga: la spunta (si crea o no), il titolo pulito e quattro etichette
 * che si toccano: cliente, chi lo fa, scadenza, priorità. Quello che la riga
 * non dice prende il valore della finestra ("Assegnato a", "Scadenza",
 * "Cliente") ed è scritto in grigio. I dubbi del divisore stanno sotto la
 * riga: niente viene scelto in silenzio.
 *
 * Computer: un clic sull'etichetta apre un menu piccolo con le stesse scelte
 * delle tendine. Telefono: la riga è come quelle del kit (MRow) con la spunta
 * a sinistra; toccandola sale "Questo task" con Cliente, Chi lo fa, Scadenza
 * e Priorità, che aprono MScelte o MCampo (os-planning/mobile.md §12).
 *
 * Con una proposta dell'Assistente (28/10/2026, os-planning/ai/fase-1-progetto.md,
 * decisione 11) le righe arrivano da POST /ai/dividi e non dal divisore a regole.
 * Quello che ha proposto l'Assistente è in grigio come quello preso dalla finestra
 * (origine 'assistente'); le righe che lui propone di non creare partono senza
 * spunta, col motivo sotto il titolo; la descrizione proposta si legge sotto il
 * titolo e va nel task. In più: «Letto prima di dividere», il gruppo «Cose da
 * aggiungere alla scheda di …» e, da «Dividi in più task», la spunta che completa
 * la task di partenza.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  DUBBIO_MANCA_COSA_FARE, DUBBIO_TITOLO_ACCORCIATO, TITOLO_MAX,
  type CosaDaRicordare, type SchedaLettaDto, type TaskDiviso, type TaskDivisoAssistente,
} from '@masamune/shared';
import { Icon, type IconName } from '../ui/Icon';
import { MobileSheet } from '../ui/MobileSheet';
import { MCampo, MGroup, MRow, MScelte, MSearch } from '../mobile/MobileKit';
import type { CorpoTask } from '../../features/tasks/useTaskActions';
import { SEZIONI_MEMORIA } from '../../features/assistente/assistenteApi';
import './nuovo-task.css';

// ── I dati di una riga ────────────────────────────────────────────────────

/**
 * Da dove viene un valore: letto nella riga dal divisore, proposto dall'Assistente,
 * preso dalla finestra, scelto a mano, o niente.
 */
export type Origine = 'riga' | 'assistente' | 'finestra' | 'scelto' | 'nessuno';

/**
 * Una riga da mostrare: del divisore a regole, o proposta dall'Assistente con in più
 * la descrizione e il motivo per partire senza spunta (28/10/2026).
 */
export type Diviso = TaskDiviso & Partial<Pick<TaskDivisoAssistente, 'descrizione' | 'senzaSpunta'>>;

/** Le correzioni fatte a mano su una riga: vincono su quello che ha letto il divisore. */
export interface Scelte {
  spuntata?: boolean;
  clientId?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
  alta?: boolean;
}

export interface RigaAnteprima {
  chiave: string;
  d: Diviso;
  /** La riga viene da una proposta dell'Assistente (28/10/2026). */
  daAssistente: boolean;
  spuntata: boolean;
  clientId: string | null;
  clienteDa: Origine;
  assigneeId: string | null;
  personaDa: Origine;
  dueDate: string | null;
  dataDa: Origine;
  alta: boolean;
  /** Quello che il server rifiuterebbe: così la riga non si può creare. */
  problema: string | null;
}

/**
 * "Assegnato a", "Scadenza" e "Cliente" della finestra: valgono per le righe che non li
 * dicono. `alta`: la Priorità Alta scelta nella finestra prima di dividere (28/10/2026),
 * vale per ogni riga che non è stata rimessa a Normale a mano.
 */
export interface Predefiniti { clientId: string | null; assigneeId: string | null; dueDate: string | null; alta?: boolean }

export interface OpzioniAnteprima {
  /** 'YYYY-MM-DD' di Roma. */
  oggi: string;
  meId: string;
  /** nome intero (negli elenchi da scegliere) e breve (nelle etichette e nella riga del telefono). */
  clienti: Array<{ id: string; nome: string; breve: string }>;
  /** nome intero ("Andrea Anfuso (io)") e breve ("A. Anfuso") per la riga del telefono. */
  persone: Array<{ id: string; nome: string; breve: string }>;
}

/** Una chiave per riga che regge quando si scrive sopra o sotto: il testo, e quante volte è già comparso. */
function chiaviDi(divisi: TaskDiviso[]): string[] {
  const viste = new Map<string, number>();
  return divisi.map((d) => {
    const n = viste.get(d.testo) ?? 0;
    viste.set(d.testo, n + 1);
    return `${d.testo}#${n}`;
  });
}

// `da` è l'origine di un valore scritto nella riga: 'riga' col divisore a regole,
// 'assistente' con una proposta (28/10/2026), che si mostra in grigio come la finestra.
function valore<T>(scelto: T | null | undefined, riga: T | null, finestra: T | null, da: 'riga' | 'assistente'): [T | null, Origine] {
  if (scelto !== undefined) return [scelto, 'scelto'];
  if (riga != null) return [riga, da];
  if (finestra != null) return [finestra, 'finestra'];
  return [null, 'nessuno'];
}

export function righeAnteprima(divisi: Diviso[], scelte: Record<string, Scelte>, pred: Predefiniti, daAssistente = false): RigaAnteprima[] {
  const chiavi = chiaviDi(divisi);
  const da = daAssistente ? 'assistente' : 'riga';
  return divisi.map((d, i) => {
    const chiave = chiavi[i]!;
    const s = scelte[chiave] ?? {};
    const [clientId, clienteDa] = valore(s.clientId, d.clientId, pred.clientId, da);
    const [assigneeId, personaDa] = valore(s.assigneeId, d.assigneeId, pred.assigneeId, da);
    const [dueDate, dataDa] = valore(s.dueDate, d.dueDate, pred.dueDate, da);
    // Lo schema del server rifiuta tutto il lotto per un titolo vuoto o oltre 200 caratteri:
    // qui si vede prima, sulla riga, e il tasto resta spento.
    const titolo = d.titolo.trim();
    const problema = !titolo ? 'Manca cosa fare' : titolo.length > TITOLO_MAX ? `Troppo lunga: al massimo ${TITOLO_MAX} caratteri` : null;
    return {
      chiave, d, daAssistente, clientId, clienteDa, assigneeId, personaDa, dueDate, dataDa,
      alta: s.alta ?? (d.alta || !!pred.alta),
      // Le righe che dicono solo una data o una persona ("venerdì" da solo) partono senza spunta,
      // e così quelle che l'Assistente propone di non creare (una domanda per lui, una faccenda
      // personale, 28/10/2026): il motivo si legge sotto il titolo, «Spunta tutte» le spunta lo stesso.
      spuntata: !problema && (s.spuntata ?? (!d.dubbi.includes(DUBBIO_MANCA_COSA_FARE) && !d.senzaSpunta)),
      problema,
    };
  });
}

/** Il server accetta descrizioni fino a 5000 caratteri (createTaskInput). */
const DESCRIZIONE_MAX = 5000;

/**
 * La descrizione del task. Col divisore a regole c'è solo se il titolo è stato accorciato:
 * la riga intera. Con l'Assistente (28/10/2026) è la sua descrizione, più in fondo la riga
 * intera, «Dal dettato: …», se il titolo è stato accorciato.
 */
function descrizioneDi(r: RigaAnteprima): string | null {
  const accorciato = r.d.dubbi.includes(DUBBIO_TITOLO_ACCORCIATO);
  if (!r.daAssistente) return accorciato ? r.d.testo : null;
  const pezzi = [r.d.descrizione?.trim(), accorciato ? `Dal dettato: ${r.d.testo.trim()}` : null]
    .filter((p): p is string => !!p);
  return pezzi.length ? pezzi.join('\n\n').slice(0, DESCRIZIONE_MAX) : null;
}

/**
 * Quello che era scritto nella finestra prima dell'anteprima (28/10/2026, dalla revisione):
 * descrizione, promemoria e ricorrenza. Prima sparivano senza una parola quando arrivava la
 * proposta dell'Assistente; adesso vanno in ogni task, e l'anteprima li mostra.
 */
export interface DallaFinestra {
  /** In fondo alla descrizione di ogni task. */
  descrizione: string;
  /** ISO, o null. */
  remindAt: string | null;
  recurrence: NonNullable<CorpoTask['recurrence']>;
}

/** Il corpo di POST /tasks/bulk per una riga. */
export function corpoDi(r: RigaAnteprima, projectId: string | null, finestra?: DallaFinestra): CorpoTask {
  const scritta = finestra?.descrizione.trim();
  const description = [descrizioneDi(r), scritta].filter((p): p is string => !!p).join('\n\n').slice(0, DESCRIZIONE_MAX) || null;
  return {
    title: r.d.titolo.trim(),
    description,
    projectId,
    clientId: r.clientId,
    status: 'TODO',
    priority: r.alta ? 'ALTA' : 'MEDIA',
    assigneeId: r.assigneeId,
    dueDate: r.dueDate,
    remindAt: finestra?.remindAt ?? null,
    recurrence: finestra?.recurrence ?? 'NONE',
    isPrivate: personale(r),
  };
}

/**
 * Una faccenda personale di chi detta (ai.prompt.ts: «Faccenda personale di …»), spuntata a
 * mano o con «Spunta tutte», nasce privata (28/10/2026, dalla revisione): la villa di Andrea
 * non va nell'elenco «Tutte» della Squadra, né su Discord.
 */
const personale = (r: RigaAnteprima) => r.daAssistente && /^faccenda personale/i.test(r.d.senzaSpunta ?? '');

// ── Date e nomi ───────────────────────────────────────────────────────────

const FMT_GIORNO = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "ven 9 ott". */
export const giornoCorto = (iso: string) => FMT_GIORNO.format(new Date(`${iso}T00:00:00Z`)).replace(/\./g, '');
function piuGiorni(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const maiuscola = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const piega = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

interface Opzione { v: string; l: string; sub?: string }

/** Oggi, domani e i sei giorni dopo: le scadenze che si scelgono di più. */
function scelteDiData(oggi: string): Opzione[] {
  const domani = piuGiorni(oggi, 1);
  return [
    { v: oggi, l: 'Oggi', sub: giornoCorto(oggi) },
    { v: domani, l: 'Domani', sub: giornoCorto(domani) },
    ...[2, 3, 4, 5, 6, 7].map((n) => {
      const v = piuGiorni(oggi, n);
      return { v, l: maiuscola(giornoCorto(v)) };
    }),
  ];
}

// Forme societarie in fondo al nome, tolte una alla volta ("… Societa' a Responsabilita' Limitata Semplificata").
const FORMA_IN_FONDO = /[\s,]+(?:s\.?\s?r\.?\s?l\.?(?:\s?s\.?)?|s\.?\s?p\.?\s?a\.?|s\.?\s?n\.?\s?c\.?|s\.?\s?a\.?\s?s\.?|s\.?\s?s\.?|b\.?\s?v\.?|o[uü]|a\s+s\.?\s?u\.?|[&e]\s*c\.?|societ[aà]'?|cooperativa|benefit|semplificata|limitata|responsabilit[aà]'?|a|-)$/i;

/**
 * Il nome che sta in un'etichetta o nella riga del telefono: senza la forma
 * societaria e senza il titolare. "Montagna comunicazione di Angelica
 * Montagna" → "Montagna comunicazione", "Azienda Agricola Graci di Aiello
 * Graci Alberto S.s." → "Azienda Agricola Graci". "Dottoressa Ylenia di
 * Franco" resta com'è: dopo "di" c'è un cognome, non un titolare.
 */
export function nomeBreve(nome: string): string {
  const senzaForme = (s: string) => {
    let t = s.trim();
    for (let giro = 0; giro < 8 && FORMA_IN_FONDO.test(t); giro++) t = t.replace(FORMA_IN_FONDO, '').trim();
    return t;
  };
  let n = senzaForme(nome);
  const titolare = /^(.+?\S)\s+di\s+\S+\s+\S+.*$/i.exec(n);
  if (titolare) n = senzaForme(titolare[1]!);
  return n || nome;
}

const nomeCliente = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.clienti.find((c) => c.id === id)?.nome ?? 'Cliente' : null);
const breveCliente = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.clienti.find((c) => c.id === id)?.breve ?? 'Cliente' : null);
const nomePersona = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.persone.find((p) => p.id === id) ?? null : null);

/** Il grigio dice "preso dalla finestra": vale per le righe che non lo dicono. */
const DALLA_FINESTRA = 'Preso dalla finestra: vale per le righe che non lo dicono';
/** Lo stesso grigio per quello che ha proposto l'Assistente (28/10/2026): non l'ha scelto la persona. */
const DALL_ASSISTENTE = 'Proposto da MUNE';
/** In grigio: preso dalla finestra o proposto dall'Assistente. Le classi dicono "finestra", il grigio è uno. */
const inGrigio = (o: Origine) => o === 'finestra' || o === 'assistente';
/** Le parole del grigio, per il suggerimento e per chi legge lo schermo. */
const perche = (o: Origine) => (o === 'finestra' ? DALLA_FINESTRA : o === 'assistente' ? DALL_ASSISTENTE : null);

/**
 * Sotto il titolo: quello che il server rifiuterebbe, perché l'Assistente la lascia senza
 * spunta («Domanda per MUNE: …»), poi i dubbi del divisore o dell'Assistente.
 */
const dubbiDi = (r: RigaAnteprima) => [
  ...(r.problema ? [r.problema] : []),
  ...(r.d.senzaSpunta ? [personale(r) ? `${r.d.senzaSpunta}: se la spunti, nasce privata` : r.d.senzaSpunta] : []),
  ...r.d.dubbi,
];

// ── Computer: un menu piccolo sotto l'etichetta ──────────────────────────

/**
 * Il menu sta in un portale su <body>, fisso sullo schermo: dentro la finestra,
 * che scorre, verrebbe tagliato dal bordo. Si chiude con un clic fuori, con
 * Esc (senza chiudere la finestra sotto) o se la finestra scorre.
 */
function Menu({ ancora, onClose, children }: { ancora: HTMLElement; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | null>(null);
  const LARGO = 260;
  useLayoutEffect(() => {
    const r = ancora.getBoundingClientRect();
    const sotto = window.innerHeight - r.bottom - 16;
    const sopra = r.top - 16;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - LARGO - 8));
    if (sotto >= 280 || sotto >= sopra) setPos({ top: r.bottom + 6, left, maxHeight: Math.max(160, sotto) });
    else setPos({ bottom: window.innerHeight - r.top + 6, left, maxHeight: Math.max(160, sopra) });
  }, [ancora]);
  useEffect(() => {
    const fuori = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', fuori, true);
    return () => {
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', fuori, true);
    };
  }, [onClose]);
  useEffect(() => {
    if (!pos) return;
    const primo = ref.current?.querySelector<HTMLElement>('input, [aria-selected="true"], button');
    primo?.focus();
  }, [pos]);
  if (!pos) return null;
  return createPortal(
    <>
      <div className="nt-menu-velo" onMouseDown={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={ref}
        className="nt-menu"
        role="dialog"
        style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: LARGO, maxHeight: pos.maxHeight }}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          e.stopPropagation();
          onClose();
          ancora.focus();
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

function ListaMenu({ opzioni, valore, onScegli, cerca, vuoto, piede }: {
  opzioni: Opzione[];
  valore: string;
  onScegli: (v: string) => void;
  cerca?: string;
  vuoto?: string;
  piede?: ReactNode;
}) {
  const [q, setQ] = useState('');
  const filtrate = q.trim() ? opzioni.filter((o) => piega(o.l).includes(piega(q.trim()))) : opzioni;
  return (
    <>
      {cerca && (
        <div className="nt-menu-cerca">
          <input
            className="inp"
            placeholder={cerca}
            aria-label={cerca}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && filtrate[0]) { e.preventDefault(); onScegli(filtrate[0].v); } }}
          />
        </div>
      )}
      <div className="nt-menu-lista" role="listbox">
        {filtrate.map((o) => (
          <button key={o.v || 'nessuno'} type="button" role="option" aria-selected={o.v === valore} className="nt-menu-voce" onClick={() => onScegli(o.v)}>
            <span>{o.l}</span>
            {o.sub && <em>{o.sub}</em>}
            {o.v === valore && <Icon name="check" size={12} />}
          </button>
        ))}
        {!filtrate.length && <div className="nt-menu-vuoto">{vuoto ?? 'Niente con questo nome'}</div>}
      </div>
      {piede}
    </>
  );
}

type Campo = 'cliente' | 'persona' | 'data';

function Etichetta({ icona, testo, intero, origine, vuota, onClick, aperta, nome }: {
  icona: IconName;
  testo: string;
  /** Il valore intero, nel suggerimento, quando l'etichetta ne mostra uno breve. */
  intero?: string;
  origine: Origine;
  vuota: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  aperta: boolean;
  nome: string;
}) {
  const cls = `nt-chip${vuota ? ' is-vuota' : inGrigio(origine) ? ' is-finestra' : ''}`;
  const tutto = intero ?? testo;
  const grigio = perche(origine);
  const daDove = origine === 'finestra' ? ', preso dalla finestra' : origine === 'assistente' ? ', proposto da MUNE' : '';
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={aperta}
      aria-label={`${nome}: ${tutto}${daDove}`}
      title={grigio ? `${tutto}. ${grigio}` : tutto}
    >
      <Icon name={icona} size={11} />
      <span>{testo}</span>
    </button>
  );
}

function RigaComputer({ r, opz, onCambia }: { r: RigaAnteprima; opz: OpzioniAnteprima; onCambia: (s: Scelte) => void }) {
  const [menu, setMenu] = useState<{ campo: Campo; ancora: HTMLElement } | null>(null);
  const chiudi = useCallback(() => setMenu(null), []);
  const apri = (campo: Campo) => (e: React.MouseEvent<HTMLButtonElement>) => {
    const ancora = e.currentTarget;
    setMenu((m) => (m?.campo === campo ? null : { campo, ancora }));
  };
  const scegli = (s: Scelte) => { onCambia(s); setMenu(null); };
  const cliente = breveCliente(opz, r.clientId);
  const persona = nomePersona(opz, r.assigneeId);
  const dubbi = dubbiDi(r);
  return (
    <div className={`nt-riga${r.spuntata ? '' : ' is-spenta'}`}>
      <label className="nt-spunta" title={r.spuntata ? 'Si crea' : 'Non si crea'}>
        <input
          type="checkbox"
          checked={r.spuntata}
          disabled={!!r.problema && !r.spuntata}
          onChange={(e) => onCambia({ spuntata: e.target.checked })}
          aria-label={`Crea "${r.d.titolo}"`}
        />
      </label>
      <div className="nt-riga-corpo">
        <div className="nt-riga-titolo">{r.d.titolo || r.d.testo}</div>
        {/* La descrizione proposta dall'Assistente: va nel task e nella mail di assegnazione,
            quindi si legge prima di creare (28/10/2026). */}
        {r.d.descrizione && <div className="nt-riga-descr">{r.d.descrizione}</div>}
        <div className="nt-etichette">
          <Etichetta nome="Cliente" icona="building" testo={cliente ?? 'Nessun cliente'} intero={nomeCliente(opz, r.clientId) ?? undefined} origine={r.clienteDa} vuota={!cliente} aperta={menu?.campo === 'cliente'} onClick={apri('cliente')} />
          <Etichetta nome="Chi lo fa" icona="user" testo={persona?.nome ?? 'Nessuno'} origine={r.personaDa} vuota={!persona} aperta={menu?.campo === 'persona'} onClick={apri('persona')} />
          <Etichetta nome="Scadenza" icona="calendar" testo={r.dueDate ? giornoCorto(r.dueDate) : 'Senza scadenza'} origine={r.dataDa} vuota={!r.dueDate} aperta={menu?.campo === 'data'} onClick={apri('data')} />
          <button
            type="button"
            className={`nt-chip nt-chip-alta${r.alta ? '' : ' is-vuota'}`}
            aria-pressed={r.alta}
            aria-label={r.alta ? 'Priorità alta: tocca per tornare normale' : 'Priorità normale: tocca per renderla alta'}
            onClick={() => onCambia({ alta: !r.alta })}
          >
            <Icon name="arrowUp" size={11} />
            <span>{r.alta ? 'Alta' : 'Normale'}</span>
          </button>
        </div>
        {dubbi.map((d, i) => (
          <div key={`${i}-${d}`} className={`nt-dubbio${d === r.problema ? ' is-blocco' : ''}`}>{d}</div>
        ))}
      </div>
      {menu && (
        <Menu ancora={menu.ancora} onClose={chiudi}>
          {menu.campo === 'cliente' && (
            <ListaMenu
              cerca="Cerca un cliente"
              vuoto="Nessun cliente con questo nome"
              opzioni={[{ v: '', l: 'Nessun cliente' }, ...opz.clienti.map((c) => ({ v: c.id, l: c.nome }))]}
              valore={r.clientId ?? ''}
              onScegli={(v) => scegli({ clientId: v || null })}
            />
          )}
          {menu.campo === 'persona' && (
            <ListaMenu
              opzioni={[{ v: '', l: 'Nessuno' }, ...opz.persone.map((p) => ({ v: p.id, l: p.nome }))]}
              valore={r.assigneeId ?? ''}
              onScegli={(v) => scegli({ assigneeId: v || null })}
            />
          )}
          {menu.campo === 'data' && (
            <ListaMenu
              opzioni={[{ v: '', l: 'Senza scadenza' }, ...scelteDiData(opz.oggi)]}
              valore={r.dueDate ?? ''}
              onScegli={(v) => scegli({ dueDate: v || null })}
              piede={(
                <label className="nt-menu-data">
                  <span>Un altro giorno</span>
                  <input
                    className="inp mono"
                    type="date"
                    defaultValue={r.dueDate ?? ''}
                    onChange={(e) => { if (e.target.value) scegli({ dueDate: e.target.value }); }}
                  />
                </label>
              )}
            />
          )}
        </Menu>
      )}
    </div>
  );
}

// ── Telefono: la riga del kit con la spunta, e "Questo task" dal basso ──────

function RigaTelefono({ r, opz, onCambia, onApri }: {
  r: RigaAnteprima;
  opz: OpzioniAnteprima;
  onCambia: (s: Scelte) => void;
  onApri: () => void;
}) {
  const persona = nomePersona(opz, r.assigneeId);
  const pezzi: Array<{ t: string; grigio: boolean }> = [];
  const cliente = breveCliente(opz, r.clientId);
  if (cliente) pezzi.push({ t: cliente, grigio: inGrigio(r.clienteDa) });
  if (persona) pezzi.push({ t: persona.breve, grigio: inGrigio(r.personaDa) });
  if (r.dueDate) pezzi.push({ t: giornoCorto(r.dueDate), grigio: inGrigio(r.dataDa) });
  if (r.alta) pezzi.push({ t: 'Alta', grigio: false });
  const dubbi = dubbiDi(r);
  return (
    <div className={`nt-m-riga${r.spuntata ? '' : ' is-spenta'}`}>
      <button
        type="button"
        className="nt-m-spunta"
        role="checkbox"
        aria-checked={r.spuntata}
        aria-label={r.spuntata ? `Non creare "${r.d.titolo}"` : `Crea "${r.d.titolo}"`}
        disabled={!!r.problema && !r.spuntata}
        onClick={() => onCambia({ spuntata: !r.spuntata })}
      >
        <span>{r.spuntata && <Icon name="check" size={14} strokeWidth={2.5} />}</span>
      </button>
      <button type="button" className="mk-row nt-m-corpo" onClick={onApri}>
        <span className="mk-main">
          <span className="mk-title">{r.d.titolo || r.d.testo}</span>
          <span className="mk-sub">
            {pezzi.length
              ? pezzi.map((p, i) => <span key={i} className={p.grigio ? 'nt-m-finestra' : 'is-ink'}>{i > 0 && ' · '}{p.t}</span>)
              : 'Senza cliente, persona e scadenza'}
          </span>
          {dubbi.length > 0 && <span className="nt-m-dubbio">{dubbi[0]}{dubbi.length > 1 ? ` (e altri ${dubbi.length - 1})` : ''}</span>}
        </span>
        <Icon name="chevronR" size={13} className="mk-chev" />
      </button>
    </div>
  );
}

function FoglioClienti({ open, onClose, opz, valore, onScegli }: {
  open: boolean;
  onClose: () => void;
  opz: OpzioniAnteprima;
  valore: string;
  onScegli: (v: string) => void;
}) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  const tutti: Opzione[] = [{ v: '', l: 'Nessun cliente' }, ...opz.clienti.map((c) => ({ v: c.id, l: c.nome }))];
  const filtrate = q.trim() ? tutti.filter((o) => piega(o.l).includes(piega(q.trim()))) : tutti;
  return (
    <MobileSheet open={open} onClose={onClose} title="Cliente">
      <div className="nt-m-cerca"><MSearch value={q} onChange={setQ} placeholder="Cerca un cliente" /></div>
      <div className="mk-list">
        {filtrate.map((o) => (
          <MRow key={o.v || 'nessuno'} title={o.l} trail={o.v === valore ? <Icon name="check" size={16} /> : undefined} chevron={false} onClick={() => onScegli(o.v)} />
        ))}
        {!filtrate.length && <div className="mk-empty">Nessun cliente con questo nome.</div>}
      </div>
    </MobileSheet>
  );
}

type Vista = 'riga' | 'cliente' | 'persona' | 'data' | 'altra-data' | 'priorita';

function QuestoTask({ r, opz, onCambia, onClose }: {
  r: RigaAnteprima;
  opz: OpzioniAnteprima;
  onCambia: (s: Scelte) => void;
  onClose: () => void;
}) {
  const [vista, setVista] = useState<Vista>('riga');
  const [altraData, setAltraData] = useState(r.dueDate ?? opz.oggi);
  const torna = useCallback(() => setVista('riga'), []);
  const cliente = nomeCliente(opz, r.clientId);
  const persona = nomePersona(opz, r.assigneeId);
  const dubbi = dubbiDi(r);
  const origini = [r.clienteDa, r.personaDa, r.dataDa];
  const grigio = origini.some(inGrigio);
  const valoreRiga = (testo: string | null, origine: Origine, vuoto: string) => (
    <span className={`nt-m-valore${!testo ? ' is-vuoto' : inGrigio(origine) ? ' is-finestra' : ''}`}>{testo ?? vuoto}</span>
  );
  return (
    <>
      <MobileSheet
        open
        onClose={onClose}
        title="Questo task"
        footer={(
          <button type="button" className="btn m-block" disabled={!!r.problema && !r.spuntata} onClick={() => { onCambia({ spuntata: !r.spuntata }); onClose(); }}>
            {r.spuntata ? 'Non crearlo' : 'Crealo'}
          </button>
        )}
      >
        <div className="nt-m-foglio">
          <div className="nt-m-foglio-titolo">{r.d.titolo || r.d.testo}</div>
          {r.d.descrizione && <div className="nt-riga-descr">{r.d.descrizione}</div>}
          {dubbi.map((d, i) => <div key={`${i}-${d}`} className={`nt-dubbio${d === r.problema ? ' is-blocco' : ''}`}>{d}</div>)}
          <div className="mk-list">
            <MRow title="Cliente" trail={valoreRiga(cliente, r.clienteDa, 'Nessuno')} onClick={() => setVista('cliente')} />
            <MRow title="Chi lo fa" trail={valoreRiga(persona?.nome ?? null, r.personaDa, 'Nessuno')} onClick={() => setVista('persona')} />
            <MRow title="Scadenza" trail={valoreRiga(r.dueDate ? giornoCorto(r.dueDate) : null, r.dataDa, 'Nessuna')} onClick={() => setVista('data')} />
            <MRow title="Priorità" trail={valoreRiga(r.alta ? 'Alta' : 'Normale', 'riga', '')} onClick={() => setVista('priorita')} />
          </div>
          {grigio && (
            <div className="mk-group-note">
              {origini.includes('assistente')
                ? 'In grigio quello proposto da MUNE o preso dalla finestra.'
                : 'In grigio quello preso dalla finestra: vale per le righe che non lo dicono.'}
            </div>
          )}
        </div>
      </MobileSheet>
      <FoglioClienti open={vista === 'cliente'} onClose={torna} opz={opz} valore={r.clientId ?? ''} onScegli={(v) => { onCambia({ clientId: v || null }); torna(); }} />
      <MScelte
        open={vista === 'persona'}
        onClose={torna}
        titolo="Chi lo fa"
        opzioni={[{ v: '', l: 'Nessuno' }, ...opz.persone.map((p) => ({ v: p.id, l: p.nome }))]}
        valore={r.assigneeId ?? ''}
        onScegli={(v) => { onCambia({ assigneeId: v || null }); torna(); }}
      />
      <MScelte
        open={vista === 'data'}
        onClose={torna}
        titolo="Scadenza"
        opzioni={[{ v: '', l: 'Senza scadenza' }, ...scelteDiData(opz.oggi), { v: 'altra', l: 'Un altro giorno' }]}
        valore={r.dueDate ?? ''}
        onScegli={(v) => {
          if (v === 'altra') { setAltraData(r.dueDate ?? opz.oggi); setVista('altra-data'); return; }
          onCambia({ dueDate: v || null });
          torna();
        }}
      />
      <MCampo
        open={vista === 'altra-data'}
        onClose={torna}
        titolo="Scadenza"
        campo={<input className="inp" type="date" aria-label="Scadenza" value={altraData} onChange={(e) => setAltraData(e.target.value)} />}
        onSalva={() => { if (altraData) onCambia({ dueDate: altraData }); torna(); }}
      />
      <MScelte
        open={vista === 'priorita'}
        onClose={torna}
        titolo="Priorità"
        opzioni={[{ v: 'MEDIA', l: 'Normale' }, { v: 'ALTA', l: 'Alta' }]}
        valore={r.alta ? 'ALTA' : 'MEDIA'}
        onScegli={(v) => { onCambia({ alta: v === 'ALTA' }); torna(); }}
      />
    </>
  );
}

// ── Con una proposta dell'Assistente (28/10/2026) ─────────────────────────

/** Quello che arriva con una proposta dell'Assistente, oltre alle task. */
export interface ExtraProposta {
  /** Le schede lette prima di dividere. */
  lette: SchedaLettaDto[];
  /** Divisa per prova, senza Claude (in locale, senza chiave): l'anteprima lo dice. */
  finta: boolean;
  /** Le cose da aggiungere alle schede, nell'ordine della proposta: al server vanno gli indici. */
  ricorda: CosaDaRicordare[];
  /** Gli indici tolti a mano: le altre restano spuntate. */
  ricordaTolte: ReadonlySet<number>;
  onRicorda: (indice: number, spuntata: boolean) => void;
  /**
   * «Dividi in più task»: la spunta che completa la task di partenza; null se non c'è una
   * partenza. `nota`: cosa succede completandola, quando non è solo chiuderla (un lavoro
   * previsto, una task che si ripete; 28/10/2026).
   */
  completa: { spuntata: boolean; onCambia: (spuntata: boolean) => void; nota?: string | null } | null;
}

/** Il nome breve del cliente se lo conosciamo («Xenia»), se no quello che dice il server. */
const nomeScheda = (opz: OpzioniAnteprima, tipo: 'cliente' | 'progetto', id: string, nome: string) =>
  (tipo === 'cliente' ? opz.clienti.find((c) => c.id === id)?.breve : null) ?? nome;

/** "Xenia", "Xenia ed Elios", "Xenia, Elios e Graci". */
function elenco(nomi: string[]): string {
  if (nomi.length < 2) return nomi.join('');
  const ultimo = nomi[nomi.length - 1]!;
  return `${nomi.slice(0, -1).join(', ')} ${/^[eèé]/i.test(ultimo) ? 'ed' : 'e'} ${ultimo}`;
}

/**
 * «Letto prima di dividere: schede di Xenia ed Elios». Solo testo: il pannello
 * dell'Assistente starebbe dietro la finestra (decisione 11). Le schede vuote non si
 * nominano, come nel pannello.
 */
function fraseLette(lette: SchedaLettaDto[], opz: OpzioniAnteprima): string | null {
  const nomi = lette.filter((s) => s.righe > 0).map((s) => nomeScheda(opz, s.tipo, s.id, s.nome));
  if (!nomi.length) return null;
  return `Letto prima di dividere: ${nomi.length === 1 ? 'scheda' : 'schede'} di ${elenco(nomi)}`;
}

/** Le cose da ricordare divise per cliente, nell'ordine in cui arrivano, con il loro indice. */
function perScheda(ricorda: CosaDaRicordare[], opz: OpzioniAnteprima) {
  const gruppi = new Map<string, { id: string; nome: string; righe: Array<{ i: number; c: CosaDaRicordare }> }>();
  ricorda.forEach((c, i) => {
    const g = gruppi.get(c.clientId) ?? { id: c.clientId, nome: nomeScheda(opz, 'cliente', c.clientId, c.cliente), righe: [] };
    g.righe.push({ i, c });
    gruppi.set(c.clientId, g);
  });
  return [...gruppi.values()];
}

const nomeSezione = (codice: string) => SEZIONI_MEMORIA.find((s) => s.codice === codice)?.nome ?? '';

/**
 * «Cose da aggiungere alla scheda di Xenia» (1.2): sotto le task, una per riga con la
 * spunta. I testi non si cambiano qui: il server scrive quelli che ha proposto lui.
 * Computer: righe come quelle dei task. Telefono: un gruppo del kit, la spunta a sinistra.
 */
function CoseDaRicordare({ p, opz, mobile }: { p: ExtraProposta; opz: OpzioniAnteprima; mobile: boolean }) {
  const gruppi = perScheda(p.ricorda, opz);
  if (!gruppi.length) return null;
  return (
    <>
      {gruppi.map((g) => {
        const titolo = `Cose da aggiungere alla scheda di ${g.nome}`;
        if (mobile) {
          return (
            <div key={g.id} className="nt-m-gruppo">
              <MGroup title={titolo}>
                {g.righe.map(({ i, c }) => {
                  const on = !p.ricordaTolte.has(i);
                  return (
                    <div key={i} className={`nt-m-riga nt-m-ricorda${on ? '' : ' is-spenta'}`}>
                      <button
                        type="button"
                        className="nt-m-spunta"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={on ? `Non ricordare "${c.testo}"` : `Ricorda "${c.testo}"`}
                        onClick={() => p.onRicorda(i, !on)}
                      >
                        <span>{on && <Icon name="check" size={14} strokeWidth={2.5} />}</span>
                      </button>
                      {/* Anche il testo mette e toglie la spunta: è il bersaglio più grande. */}
                      <button type="button" className="mk-row nt-m-corpo" tabIndex={-1} aria-hidden="true" onClick={() => p.onRicorda(i, !on)}>
                        <span className="mk-main">
                          <span className="mk-title">{c.testo}</span>
                          <span className="mk-sub">{nomeSezione(c.sezione)}</span>
                        </span>
                      </button>
                    </div>
                  );
                })}
              </MGroup>
            </div>
          );
        }
        return (
          <div key={g.id} className="nt-ricorda" role="group" aria-label={titolo}>
            <div className="nt-ricorda-hd">{titolo}</div>
            {g.righe.map(({ i, c }) => {
              const on = !p.ricordaTolte.has(i);
              return (
                <label key={i} className={`nt-riga nt-ricorda-riga${on ? '' : ' is-spenta'}`}>
                  <span className="nt-spunta" title={on ? 'Si aggiunge alla scheda' : 'Non si aggiunge'}>
                    <input type="checkbox" checked={on} onChange={(e) => p.onRicorda(i, e.target.checked)} />
                  </span>
                  <span className="nt-riga-corpo">
                    <span className="nt-ricorda-testo">{c.testo}</span>
                    <span className="nt-ricorda-sezione">{nomeSezione(c.sezione)}</span>
                  </span>
                </label>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

/**
 * «Completa la task di partenza quando crei queste» (1.3), in fondo e già spuntata; per un
 * lavoro previsto parte tolta, con la nota sotto (28/10/2026). Senza task spuntati è
 * spenta: senza task nuovi la partenza non si completa.
 */
function CompletaPartenza({ c, attiva, mobile }: { c: NonNullable<ExtraProposta['completa']>; attiva: boolean; mobile: boolean }) {
  const testo = 'Completa la task di partenza quando crei queste';
  if (mobile) {
    return (
      <div className="mk-list nt-m-completa">
        <div className={`nt-m-riga${c.spuntata && attiva ? '' : ' is-spenta'}`}>
          <button
            type="button"
            className="nt-m-spunta"
            role="checkbox"
            aria-checked={c.spuntata}
            aria-label={c.nota ? `${testo}. ${c.nota}` : testo}
            disabled={!attiva}
            onClick={() => c.onCambia(!c.spuntata)}
          >
            <span>{c.spuntata && <Icon name="check" size={14} strokeWidth={2.5} />}</span>
          </button>
          <button type="button" className="mk-row nt-m-corpo" tabIndex={-1} aria-hidden="true" disabled={!attiva} onClick={() => c.onCambia(!c.spuntata)}>
            <span className="mk-main">
              <span className="mk-title">{testo}</span>
              {c.nota && <span className="nt-m-dubbio">{c.nota}</span>}
            </span>
          </button>
        </div>
      </div>
    );
  }
  return (
    <label className={`nt-completa${attiva ? '' : ' is-spenta'}`}>
      <span className="nt-spunta">
        <input type="checkbox" checked={c.spuntata} disabled={!attiva} onChange={(e) => c.onCambia(e.target.checked)} />
      </span>
      <span className="nt-completa-testo">
        <span>{testo}</span>
        {c.nota && <span className="nt-dubbio">{c.nota}</span>}
      </span>
    </label>
  );
}

// ── L'anteprima intera ────────────────────────────────────────────────────

export function AnteprimaTask({ righe, opz, mobile, onCambia, proposta }: {
  righe: RigaAnteprima[];
  opz: OpzioniAnteprima;
  mobile: boolean;
  /** Una correzione su una riga (chiave), oppure su tutte (null). */
  onCambia: (chiave: string | null, s: Scelte) => void;
  /** Con una proposta dell'Assistente: schede lette, cose da ricordare, task di partenza. */
  proposta?: ExtraProposta;
}) {
  const [aperta, setAperta] = useState<string | null>(null);
  const rigaAperta = aperta ? righe.find((r) => r.chiave === aperta) ?? null : null;
  const spuntate = righe.filter((r) => r.spuntata).length;
  const daControllare = righe.filter((r) => r.problema || r.d.senzaSpunta || r.d.dubbi.length).length;
  const tutte = spuntate === righe.length;
  const lette = proposta ? fraseLette(proposta.lette, opz) : null;
  return (
    <section className="nt-anteprima" aria-label="I task che si creano">
      <div className="nt-anteprima-hd">
        <span>
          <strong>
            {!righe.length ? 'Nessun task da creare'
              : tutte ? `${righe.length} task da creare`
              : `${spuntate} task da creare su ${righe.length}`}
          </strong>
          {daControllare > 0 && <span className="nt-anteprima-dubbi"> · {daControllare === 1 ? 'uno' : daControllare} da controllare</span>}
        </span>
        {righe.length > 0 && (
          <button type="button" className="nt-tutte" onClick={() => onCambia(null, { spuntata: !tutte })}>
            {tutte ? 'Togli tutte le spunte' : 'Spunta tutte'}
          </button>
        )}
      </div>
      {proposta && (proposta.finta || lette) && (
        <div className="nt-lette">
          {proposta.finta && <div>In prova: divisione finta, fatta senza Claude.</div>}
          {lette && <div>{lette}</div>}
        </div>
      )}
      {/* Una proposta può avere solo cose da ricordare: niente elenco vuoto. */}
      {righe.length > 0 && (mobile ? (
        <div className="nt-m-lista">
          {righe.map((r) => (
            <RigaTelefono key={r.chiave} r={r} opz={opz} onCambia={(s) => onCambia(r.chiave, s)} onApri={() => setAperta(r.chiave)} />
          ))}
        </div>
      ) : (
        <div className="nt-lista">
          {righe.map((r) => <RigaComputer key={r.chiave} r={r} opz={opz} onCambia={(s) => onCambia(r.chiave, s)} />)}
        </div>
      ))}
      {proposta && <CoseDaRicordare p={proposta} opz={opz} mobile={mobile} />}
      {proposta?.completa && <CompletaPartenza c={proposta.completa} attiva={spuntate > 0} mobile={mobile} />}
      {mobile && rigaAperta && (
        <QuestoTask r={rigaAperta} opz={opz} onCambia={(s) => onCambia(rigaAperta.chiave, s)} onClose={() => setAperta(null)} />
      )}
    </section>
  );
}
